import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchSubscriptionTypesAsync,
  updateStudentSubscriptionAsync,
  deleteStudentSubscriptionAsync,
  computeEndDateAsync,
} from '../store/slices/studentsSlice';
import type { StudentSubscription } from '../store/slices/studentsSlice';
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';
import { formatDateEuropean } from '../utils/dateUtils';
import SubscriptionPeriodPicker, { type SubscriptionBusyPeriod } from './SubscriptionPeriodPicker';
import './GroupForm.css';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

function normDateOnly(d: string | null | undefined): string {
  if (!d) return '';
  return d.includes('T') ? d.split('T')[0] : d;
}

// Subscriptions are student-scoped — every other subscription of the student is a busy period.
function buildBusyPeriods(
  subs: StudentSubscription[],
  excludeSubscriptionId: number
): SubscriptionBusyPeriod[] {
  return subs
    .filter((s) => s.id !== excludeSubscriptionId)
    .map((s) => {
      const end = s.effectiveEndDate ?? s.endDate ?? s.estimatedEndDate;
      if (!s.startDate || !end) return null;
      return { start: normDateOnly(s.startDate), end: normDateOnly(end) };
    })
    .filter((p): p is SubscriptionBusyPeriod => p != null);
}

const EditSubscription: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { studentId, subscriptionId } = useParams<{ studentId: string; subscriptionId: string }>();
  const token = useAppSelector((state) => state.auth?.token);
  const { subscriptionTypes } = useAppSelector((state) => state.students);

  const [studentName, setStudentName] = useState('');
  const [groupName, setGroupName] = useState('');
  const [typeName, setTypeName] = useState('');
  const [form, setForm] = useState({
    startDate: '',
    lessonsCount: '',
    totalAmount: '',
    paymentDate: '',
    endDate: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  /** Persisted estimate from server (when no manual end) */
  const [storedEstimatedEndDate, setStoredEstimatedEndDate] = useState<string | null>(null);
  /** Live preview from compute-end-date API */
  const [previewEstimatedEndDate, setPreviewEstimatedEndDate] = useState<string | null>(null);
  const [busyPeriods, setBusyPeriods] = useState<SubscriptionBusyPeriod[]>([]);

  const hasManualEnd = Boolean(form.endDate?.trim());

  useEffect(() => {
    if (subscriptionTypes.length === 0) {
      dispatch(fetchSubscriptionTypesAsync());
    }
  }, [dispatch, subscriptionTypes.length]);

  useEffect(() => {
    if (!studentId || !subscriptionId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(
          `${API_BASE_URL}/students/${studentId}/subscriptions/${subscriptionId}`,
          { headers }
        );
        const sub = response.data.subscription;
        const total = sub.totalAmount
          ? parseFloat(sub.totalAmount).toFixed(2)
          : (sub.lessonsCount * parseFloat(sub.pricePerLesson)).toFixed(2);
        setForm({
          startDate: sub.startDate,
          lessonsCount: sub.lessonsCount.toString(),
          totalAmount: total,
          paymentDate: sub.paymentDate || '',
          endDate: normDateOnly(sub.endDate),
        });
        setTypeName(sub.subscriptionType?.name || '—');
        setStoredEstimatedEndDate(sub.estimatedEndDate ? normDateOnly(sub.estimatedEndDate) : null);
        setPreviewEstimatedEndDate(null);

        const studentRes = await axios.get(`${API_BASE_URL}/students/${studentId}`, { headers });
        const s = studentRes.data.student;
        setStudentName(`${s.firstName} ${s.lastName}${s.surname ? ' ' + s.surname : ''}`);
        const g = s.groups && s.groups.length ? s.groups[0] : null;
        setGroupName(g ? (g.name || g.level?.toUpperCase() || '—') : 'No group');
      } catch (err: unknown) {
        const msg = err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
        setError(msg || 'Failed to load subscription');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [studentId, subscriptionId, token]);

  // Other subscriptions of the student (gray), excluding this row
  useEffect(() => {
    let cancelled = false;
    if (!studentId || !subscriptionId || !token) {
      Promise.resolve().then(() => {
        if (!cancelled) setBusyPeriods([]);
      });
      return () => {
        cancelled = true;
      };
    }
    const sid = parseInt(subscriptionId, 10);
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API_BASE_URL}/students/${studentId}/subscriptions`, { headers });
        const subs = res.data.subscriptions as StudentSubscription[];
        if (!cancelled) setBusyPeriods(buildBusyPeriods(subs, sid));
      } catch {
        if (!cancelled) setBusyPeriods([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId, subscriptionId, token]);

  // Preview estimated end only when manual end is not set
  useEffect(() => {
    if (hasManualEnd || !studentId || !form.startDate || !form.lessonsCount) {
      let cancelled = false;
      Promise.resolve().then(() => {
        if (!cancelled) setPreviewEstimatedEndDate(null);
      });
      return () => {
        cancelled = true;
      };
    }
    const timer = setTimeout(() => {
      dispatch(computeEndDateAsync({
        studentId: parseInt(studentId, 10),
        startDate: form.startDate,
        lessonsCount: parseInt(form.lessonsCount, 10),
      })).unwrap().then((payload) => {
        if (payload.estimatedEndDate) setPreviewEstimatedEndDate(payload.estimatedEndDate);
      }).catch(() => {});
    }, 400);
    return () => clearTimeout(timer);
  }, [studentId, form.startDate, form.lessonsCount, hasManualEnd, dispatch]);

  const computedPricePerLesson = useMemo(() => {
    const lessons = parseInt(form.lessonsCount, 10);
    const total = parseFloat(form.totalAmount);
    if (!lessons || lessons <= 0 || !total || isNaN(total)) return null;
    return (total / lessons).toFixed(2);
  }, [form.lessonsCount, form.totalAmount]);

  const estimatedDisplay = hasManualEnd
    ? null
    : (previewEstimatedEndDate ?? storedEstimatedEndDate);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handlePeriodChange = useCallback((next: { startDate: string; endDate: string }) => {
    setForm(prev => ({ ...prev, startDate: next.startDate, endDate: next.endDate }));
    setFormErrors(prev => ({ ...prev, startDate: '', endDate: '' }));
  }, []);

  const validate = () => {
    const errors: Record<string, string> = {};
    if (!form.startDate) errors.startDate = 'Required';
    if (!form.lessonsCount || parseInt(form.lessonsCount, 10) < 1) errors.lessonsCount = 'Min 1';
    if (!form.totalAmount || parseFloat(form.totalAmount) < 0) errors.totalAmount = 'Required';
    if (!form.paymentDate) errors.paymentDate = 'Required';
    const manual = form.endDate?.trim();
    if (manual && form.startDate && manual < form.startDate) {
      errors.endDate = 'Must be on or after start date';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate() || !studentId || !subscriptionId) return;
    setSubmitting(true);
    setError(null);
    try {
      const manual = form.endDate?.trim();
      await dispatch(updateStudentSubscriptionAsync({
        studentId: parseInt(studentId, 10),
        subscriptionId: parseInt(subscriptionId, 10),
        data: {
          startDate: form.startDate,
          lessonsCount: parseInt(form.lessonsCount, 10),
          totalAmount: parseFloat(form.totalAmount),
          paymentDate: form.paymentDate,
          endDate: manual ? manual : null,
        },
      })).unwrap();
      navigate('/subscriptions');
    } catch (err: unknown) {
      setError(typeof err === 'string' ? err : 'Failed to update subscription');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!studentId || !subscriptionId) return;
    setSubmitting(true);
    setError(null);
    try {
      await dispatch(deleteStudentSubscriptionAsync({
        studentId: parseInt(studentId, 10),
        subscriptionId: parseInt(subscriptionId, 10),
      })).unwrap();
      navigate('/subscriptions');
    } catch (err: unknown) {
      setError(typeof err === 'string' ? err : 'Failed to delete subscription');
    } finally {
      setSubmitting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="group-form-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Edit Subscription</h1>
          {studentName && <p>{studentName}</p>}
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ margin: '0 2rem 1rem' }}>
          {error}
          <button type="button" onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      <div className="group-form" style={{ margin: '0 2rem' }}>
        <div className="form-section">
          <h2>Subscription Details</h2>

          <div className="form-row-compact">
            <div className="form-group">
              <label>Current Group</label>
              <input type="text" className="form-input" value={groupName} disabled />
            </div>
            <div className="form-group">
              <label>Type</label>
              <input type="text" className="form-input" value={typeName} disabled />
            </div>
            <div className="form-group">
              <label>Payment Date *</label>
              <input
                type="date"
                name="paymentDate"
                value={form.paymentDate}
                onChange={handleChange}
                className={`form-input ${formErrors.paymentDate ? 'error' : ''}`}
              />
              {formErrors.paymentDate && <span className="error-text">{formErrors.paymentDate}</span>}
            </div>
          </div>

          <SubscriptionPeriodPicker
            startDate={form.startDate}
            endDate={form.endDate}
            onChange={handlePeriodChange}
            busyPeriods={busyPeriods}
            errors={{ startDate: formErrors.startDate, endDate: formErrors.endDate }}
          />

          <div className="form-row-compact">
            <div className="form-group">
              <label>Lessons *</label>
              <input
                type="number"
                name="lessonsCount"
                value={form.lessonsCount}
                onChange={handleChange}
                className={`form-input ${formErrors.lessonsCount ? 'error' : ''}`}
                min="1"
              />
              {formErrors.lessonsCount && <span className="error-text">{formErrors.lessonsCount}</span>}
            </div>
            <div className="form-group">
              <label>Total Amount (BYN) *</label>
              <input
                type="number"
                name="totalAmount"
                value={form.totalAmount}
                onChange={handleChange}
                className={`form-input ${formErrors.totalAmount ? 'error' : ''}`}
                min="0"
                step="0.01"
              />
              {formErrors.totalAmount && <span className="error-text">{formErrors.totalAmount}</span>}
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', padding: '0.75rem 0', fontSize: '0.95rem', color: '#4a5568' }}>
            <span><strong>Price per Lesson:</strong> {computedPricePerLesson ?? '—'} BYN</span>
            <span><strong>Estimated end date:</strong> {hasManualEnd ? '—' : (estimatedDisplay ? formatDateEuropean(estimatedDisplay) : '—')}</span>
          </div>
          <p style={{ fontSize: '0.85rem', color: '#718096', margin: '0 0 0.5rem' }}>
            Manual end (optional) in the period above overrides the schedule-based estimate.
          </p>
        </div>

        <div className="form-actions">
          <button type="button" onClick={() => navigate('/subscriptions')} className="btn-secondary">
            Cancel
          </button>
          <button type="button" onClick={handleSave} className="btn-success" disabled={submitting}>
            {submitting ? 'Saving...' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            className="btn-danger"
            disabled={submitting}
            style={{ marginLeft: 'auto' }}
          >
            Delete
          </button>
        </div>

        {showDeleteConfirm && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 'var(--radius)' }}>
            <p style={{ margin: '0 0 0.75rem', color: '#c53030' }}>Are you sure you want to delete this subscription?</p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" onClick={handleDelete} className="btn-danger" disabled={submitting}>
                {submitting ? 'Deleting...' : 'Yes, Delete'}
              </button>
              <button type="button" onClick={() => setShowDeleteConfirm(false)} className="btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditSubscription;
